import React from "react";
import Typed from "typed.js";
import cj from "../../assets/images/coder_setup_avatar.png";
import { useEffect, useState, useRef } from "react";
import ResumeModal from "./ResumeModal.jsx";
import { motion } from "framer-motion";
import ResumeSDE from '../../assets/Chahat_Resume_SDE.pdf';
import ResumeSalesforce from '../../assets/Chahat_Resume_Salesforce.pdf';
import ResumeAutomation from '../../assets/Chahat_Resume_Automation.pdf';
import useBlogStore from "../../store/useblogStore.js";
import { Briefcase, Bot, Cloud, Star, Code } from 'lucide-react';
import axios from "../../api/axiosInstance";

const Home = () => {
  const el = useRef(null);
  const [selectedResume, setSelectedResume] = useState({
    name: "Resume (SDE)",
    url: ResumeSDE,
    icon: <Code className="w-4 h-4 text-cyan-400" />
  });
  const [showModal, setShowModal] = useState(false);
  const { fetchBlogs, loaded } = useBlogStore();

  useEffect(() => {
    if (!loaded) fetchBlogs();
  }, [loaded, fetchBlogs]);

  useEffect(() => {
    const typed = new Typed(el.current, {
      strings: [
        "Software Engineer",
        "Full Stack Developer",
        "Salesforce Certified PD1",
        "Salesforce AI Automation Engineer"
      ],
      typeSpeed: 50,
      loop: true,
    });
    return () => typed.destroy();
  }, []);

  const [resumeOptions, setResumeOptions] = useState([
    { name: "Resume (SDE)", url: ResumeSDE, icon: <Code className="w-4 h-4 text-cyan-400" /> },
    { name: "Resume (Salesforce)", url: ResumeSalesforce, icon: <Cloud className="w-4 h-4 text-sky-400" /> },
    { name: "Resume (Automation)", url: ResumeAutomation, icon: <Bot className="w-4 h-4 text-purple-400" /> }
  ]);

  useEffect(() => {
    async function fetchLiveResume() {
      try {
        const response = await axios.get('/publicRoute/resume');
        if (response.status === 200 && response.data && response.data.downloadURL) {
          setResumeOptions(prev => {
            if (prev.some(r => r.name === "Latest Resume (Live)")) return prev;
            return [
              ...prev,
              {
                name: "Latest Resume (Live)",
                url: response.data.downloadURL,
                icon: <Star className="w-4 h-4 text-amber-400" />
              }
            ];
          });
        }
      } catch (err) {
        console.warn("Could not load dynamic resume from server:", err.message);
      }
    }
    fetchLiveResume();
  }, []);

  const badges = [
    { text: "Salesforce PD1 Certified", icon: <Cloud className="w-4 h-4 text-blue-400" /> },
    { text: "Agentforce Specialist", icon: <Bot className="w-4 h-4 text-indigo-400" /> },
    { text: "2+ Years Experience", icon: <Briefcase className="w-4 h-4 text-cyan-400" /> }
  ];

  return (
    <>
      <section className="mesh-gradient min-h-[80vh] md:min-h-[80vh] mt-[20%] sm:mt-[12%] md:mt-[8%] lg:mt-[7%] mb-[2%] pt-24 pb-16 md:py-12 flex flex-col-reverse md:flex-row items-center justify-around gap-8 md:gap-16 lg:gap-20 relative overflow-hidden" id="home">
        {/* Subtle grid pattern background */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] opacity-20 pointer-events-none" />
        
        {/* Glowing backdrop blobs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-[100px] will-change-transform pointer-events-none animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-[100px] will-change-transform pointer-events-none animate-pulse" style={{ animationDelay: "2s" }} />

        <div className="flex-[1.4] flex flex-col justify-center px-4 md:px-6 lg:px-8 text-left items-start z-10 w-full lg:ml-[5%] xl:ml-[8%] 2xl:ml-[10%] mt-8 md:mt-0">
          <div className="text-lg md:text-xl text-gray-400 mb-3 font-medium tracking-wide flex flex-wrap items-center justify-start gap-1">
            Hello, Myself{" "}
            <span className="purple text-2xl md:text-3xl font-extrabold bg-gradient-to-r from-cyan-400 via-blue-500 to-indigo-500 bg-clip-text text-transparent ml-1">
              Chahat Jain
            </span>
          </div>
          <div className="text-2xl md:text-4xl text-white font-bold tracking-tight mb-2 text-left w-full">and I am a Passionate</div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black mt-1 mb-6 min-h-[120px] sm:min-h-[90px] md:min-h-[110px] lg:min-h-[110px] xl:min-h-[110px] 2xl:min-h-[70px] text-red-500 tracking-tight text-left w-full drop-shadow-[0_0_15px_rgba(244,63,94,0.4)] leading-tight">
            <span id="element" ref={el} className="m-0 bg-gradient-to-r from-red-500 via-rose-500 to-orange-500 bg-clip-text text-transparent"></span>
          </h2>

          <p className="text-base md:text-lg text-gray-200 max-w-xl leading-relaxed mb-8 text-left w-full">
            Building AI-powered CRM systems, scalable web applications, and enterprise automation that eliminate manual work.
          </p>

          {/* Badges Container */}
          <div className="flex flex-wrap gap-2.5 max-w-xl mb-8">
            {badges.map((badge, idx) => (
              <motion.span 
                key={idx} 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: idx * 0.05 }}
                className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 backdrop-blur-md text-xs font-semibold px-3 py-1.5 rounded-full text-cyan-300 shadow-md hover:border-cyan-400/50 hover:shadow-cyan-500/10 hover:scale-[1.03] transition-all cursor-default"
              >
                {badge.icon}
                {badge.text}
              </motion.span>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-4 mt-2 relative z-10">
            {/* View Resume Trigger */}
            <motion.button
              onClick={() => setShowModal(true)}
              whileHover={{
                scale: 1.03,
                boxShadow: "0px 0px 15px rgba(6, 182, 212, 0.3)",
              }}
              whileTap={{ scale: 0.98 }}
              className="px-6 py-2.5 text-sm font-bold text-slate-950 rounded-lg bg-gradient-to-r from-cyan-400 to-indigo-500 hover:from-cyan-300 hover:to-indigo-400 transition-all cursor-pointer"
            >
              View Resume
            </motion.button>

            {/* See Projects Trigger */}
            <motion.button
              onClick={() => {
                const element = document.getElementById("projects");
                if (element) {
                  element.scrollIntoView({ behavior: "smooth" });
                } else {
                  window.location.href = "/#projects";
                }
              }}
              whileHover={{
                scale: 1.03,
                boxShadow: "0px 0px 15px rgba(255, 255, 255, 0.1)",
              }}
              whileTap={{ scale: 0.98 }}
              className="px-6 py-2.5 text-sm font-bold text-white border border-white/20 rounded-lg bg-white/5 backdrop-blur-md transition-all cursor-pointer"
            >
              See Projects
            </motion.button>
          </div>

          {/* Availability Info */}
          <div className="text-xs text-cyan-300/80 mt-4 flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping"></span>
            <span>📍 Available for Full-Time Roles | Open to Remote & Relocation</span>
          </div>

          <div className="links flex items-center gap-4 mt-8">
            <a
              href="https://www.linkedin.com/in/chahat-jain-96b1b51b9/"
              target="_blank"
              rel="noreferrer"
              aria-label="LinkedIn"
              className="inline-flex justify-center items-center bg-white/5 border border-white/10 rounded-full w-10 h-10 text-white hover:bg-cyan-500 hover:text-black hover:border-cyan-500 transition-all shadow-md"
            >
              <i className="bx bxl-linkedin text-xl"></i>
            </a>
            <a 
              href="https://www.instagram.com/jchahat.23/" 
              target="_blank"
              rel="noreferrer"
              aria-label="Instagram"
              className="inline-flex justify-center items-center bg-white/5 border border-white/10 rounded-full w-10 h-10 text-white hover:bg-pink-500 hover:text-white hover:border-pink-500 transition-all shadow-md"
            >
              <i className="bx bxl-instagram text-xl"></i>
            </a>
            <a 
              href="https://github.com/Chahatj23" 
              target="_blank"
              rel="noreferrer"
              aria-label="GitHub"
              className="inline-flex justify-center items-center bg-white/5 border border-white/10 rounded-full w-10 h-10 text-white hover:bg-indigo-500 hover:text-white hover:border-indigo-500 transition-all shadow-md"
            >
              <i className="bx bxl-github text-xl"></i>
            </a>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center p-4 relative z-10">
          <div className="relative flex items-center justify-center">
            {/* Glowing backdrop blobs specific to photo */}
            <div className="absolute w-72 h-72 bg-gradient-to-tr from-cyan-500/20 to-indigo-500/20 rounded-full blur-3xl opacity-60 animate-pulse pointer-events-none animate-pulse" />
            
            {/* Orbit lines */}
            <div className="absolute w-[280px] h-[280px] sm:w-[320px] sm:h-[320px] md:w-[360px] md:h-[360px] lg:w-[420px] lg:h-[420px] border border-cyan-500/10 rounded-full animate-spin-slow pointer-events-none" />
            <div className="absolute w-[220px] h-[220px] sm:w-[260px] sm:h-[260px] md:w-[290px] md:h-[290px] lg:w-[340px] lg:h-[340px] border border-indigo-500/10 rounded-full animate-spin-reverse-slow pointer-events-none" />
            
            {/* Tech stack orbit nodes */}
            <div className="absolute w-[280px] h-[280px] sm:w-[320px] sm:h-[320px] md:w-[360px] md:h-[360px] lg:w-[420px] lg:h-[420px] animate-spin-slow pointer-events-none">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 p-2 bg-slate-900 border border-white/10 rounded-xl shadow-lg">
                <span className="text-cyan-400 font-bold text-xs">AI</span>
              </div>
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 p-2 bg-slate-900 border border-white/10 rounded-xl shadow-lg">
                <span className="text-indigo-400 font-bold text-xs">SDE</span>
              </div>
            </div>

            {/* Photo Card Container */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              className="relative p-2.5 bg-white/5 border border-white/10 rounded-3xl shadow-2xl backdrop-blur-md overflow-hidden w-[220px] sm:w-[280px] md:w-[320px] lg:w-[380px] z-10"
            >
              <img 
                src={cj} 
                alt="Chahat Jain" 
                className="w-full h-auto object-cover rounded-2xl filter drop-shadow-[0_0_15px_rgba(6,182,212,0.2)] hover:scale-[1.02] transition-transform duration-500" 
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Resume Modal */}
      <ResumeModal
        show={showModal}
        onClose={() => setShowModal(false)}
        resumeOptions={resumeOptions}
        selectedResume={selectedResume}
        onSelectResume={setSelectedResume}
      />
    </>
  );
};

export default Home;
